<template>
  <section class="form-list detail-container" v-loading="isLoading">
    <el-card shadow="never" class="detail-card">
      <div slot="header" class="detail-header">
        <div class="detail-header-left">
          <span class="detail-title">入职邀请 #{{ invite.id }}</span>
          <el-tag :type="inviteStatusTag(invite)">{{ inviteStatusText(invite) }}</el-tag>
        </div>
        <div class="detail-header-right">
          <el-button
            v-if="canRevoke"
            type="primary"
            size="small"
            icon="el-icon-refresh-left"
            :loading="isRevoking"
            @click="revoke"
          >撤销邀请</el-button>
          <el-button size="small" icon="el-icon-back" @click="back">返回</el-button>
        </div>
      </div>

      <div v-if="invite.id" class="detail-main">
        <div class="apply-info-row">
          <span class="info-label">门店</span>
          <span class="info-value">{{ invite.store_name | formatStoreName }}</span>
        </div>
        <div class="apply-info-row">
          <span class="info-label">职位</span>
          <span class="info-value">{{ invite.role_name || '职位已删除' }}</span>
        </div>
        <div class="apply-info-row">
          <span class="info-label">邀请人</span>
          <span class="info-value">{{ invite.admin_nickname || `#${invite.add_administrator_id}` }}</span>
        </div>
        <div class="apply-info-row">
          <span class="info-label">生成时间</span>
          <span class="info-value">{{ invite.add_time | timeToYmdHi }}</span>
        </div>
        <div class="apply-info-row">
          <span class="info-label">过期时间</span>
          <span class="info-value">
            {{ invite.expire_time | timeToYmdHi }}
            <span v-if="isExpired" class="info-color">（已过期）</span>
          </span>
        </div>
        <div class="apply-info-row">
          <span class="info-label">申请进度</span>
          <span class="info-value">{{ applyStateText }}</span>
        </div>
        <div v-if="isRejectedApply" class="invite-reject-tip">
          {{ canRefillAfterReject
            ? '该申请已驳回，员工可在邀请有效期内重新填写申请，无需重新生成邀请链接。'
            : '该申请已驳回，但邀请已失效，员工需重新生成邀请链接后才能重新申请。' }}
        </div>
      </div>
      <div v-else-if="!isLoading" class="empty-text">未找到该入职邀请</div>
    </el-card>

    <el-card v-if="invite.id" shadow="never" class="detail-card">
      <div slot="header">邀请链接</div>
      <div class="invite-link-box">
        <el-input readonly :value="invite.url">
          <template slot="prepend">链接</template>
        </el-input>
        <el-button type="primary" plain @click="copyLink">复制链接</el-button>
      </div>
      <div class="qr-wrap">
        <img v-if="qrImage" :src="qrImage" alt="入职二维码"/>
        <span v-else class="info-color">二维码生成中...</span>
      </div>
      <div class="invite-detail-tip">员工微信扫码或打开链接即可进入入职申请页</div>
    </el-card>
  </section>
</template>

<script>
import QRCode from 'qrcode';
import { applyStates } from '@/enums/applyStates';
import RouteConfig from '@/router/config';
import { inviteShow, inviteRevoke } from '@/api/personnel';
import copyToClipboard from '@/utils/clipboard';
import {
  inviteStatusText,
  inviteStatusTag,
  isInviteExpired,
  isInvitePassed,
  isInviteRejected,
  canInviteRefillAfterReject,
} from '@/enums/invites';

export default {
  name: RouteConfig.administratorInviteDetail.name,
  data: () => ({
    isLoading: false,
    isRevoking: false,
    invite: {},
    qrImage: '',
  }),
  computed: {
    isExpired() {
      return !!this.invite.id && isInviteExpired(this.invite);
    },
    canRevoke() {
      return !!this.invite.id && !this.invite.is_del && !this.isExpired && !isInvitePassed(this.invite);
    },
    isRejectedApply() {
      return isInviteRejected(this.invite);
    },
    canRefillAfterReject() {
      return canInviteRefillAfterReject(this.invite);
    },
    applyStateText() {
      if (!this.invite.has_apply) return '员工尚未提交入职申请';
      for (const key in applyStates) {
        if (applyStates[key].value === Number(this.invite.apply_state)) return `已提交申请：${applyStates[key].name}`;
      }
      return '已提交申请';
    },
  },
  watch: {
    '$route'(to) {
      this.load(to);
    },
  },
  beforeMount() {
    this.load();
  },
  methods: {
    inviteStatusText,
    inviteStatusTag,
    async load(route = this.$route) {
      this.isLoading = true;
      const { isError, data } = await inviteShow(route.params.id);
      this.isLoading = false;
      if (isError) return;
      this.invite = data || {};
      this.qrImage = data && data.url ? await QRCode.toDataURL(data.url, { width: 240, margin: 1 }) : '';
    },
    copyLink() {
      if (!this.invite.url) return;
      // 与邀请列表页保持一致的复制实现
      copyToClipboard({ text: this.invite.url });
    },
    async revoke() {
      if (this.isRevoking) return;

      this.isRevoking = true;
      const { isError } = await inviteRevoke(this.invite.id);
      this.isRevoking = false;
      if (isError) return;
      this.$message.success('已撤销');
      this.load();
    },
    back() {
      RouteConfig.administratorInviteDetail.back();
    },
  },
};
</script>

<style scoped>
/* 详情页一律靠左（.form-list 已负责外边距与内边距），限宽但不居中 */
.detail-container {
  max-width: 1200px;
}

.detail-card {
  margin-bottom: 20px;
}

.detail-main {
  line-height: 1.8;
}

.detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
}

.detail-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.detail-header-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.detail-title {
  font-size: 16px;
  font-weight: bold;
}

.apply-info-row {
  display: flex;
  align-items: baseline;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.apply-info-row:last-child {
  border-bottom: none;
}

.info-label {
  flex-shrink: 0;
  width: 100px;
  text-align: right;
  color: #909399;
  margin-right: 16px;
}

.info-value {
  flex: 1;
  word-break: break-all;
}

.empty-text {
  color: #909399;
  text-align: center;
  padding: 40px 0;
}

.invite-link-box {
  display: flex;
  align-items: center;
  gap: 12px;
}

.invite-link-box .el-input {
  flex: 1;
}

.qr-wrap {
  margin-top: 14px;
  text-align: center;
}

.qr-wrap img {
  width: 240px;
  height: 240px;
}

.invite-detail-tip {
  margin-top: 10px;
  color: #909399;
  font-size: 13px;
  text-align: center;
}

.invite-reject-tip {
  margin: 8px 0 12px;
  padding: 8px 12px;
  background: #fef0f0;
  border-radius: 4px;
  color: #f56c6c;
  font-size: 13px;
  line-height: 1.6;
}
</style>
